import { useEffect, useMemo, useState, useContext } from "react";
import {
  getFilterJson,
  getScopeJson,
  processFilter,
  processScope,
} from "../AppView/AppViewUtils";
import type {
  FilterTreeNode,
  ProcessedConditionGroup,
  FilterPanelsData,
} from "../AppView/AppViewUtils.types";
import useInOutButtons from "../Findings/FindingsData/FilterInOut/useInOutButtons";
import type {
  InOutFilterData,
  InOutFilterPart,
} from "../Findings/FindingsData/FilterInOut/types";
import useAppViewNavigator from "../AppView/useAppViewNavigator";
import { ScopesContext } from "../AppView/ScopeManagement/ScopeProvider/ScopeProvider";

// A single [condition, field, value] tuple forwarded to onMultipleFilters.
export type FilterCondition = [string, string, string | string[]];

export interface UseWidgetFilterNavigatorOptions {
  filterToExecute: string | null | undefined;
  filterId: string | null | undefined;
  scopeToExecute: string | null | undefined;
  scopeId: string | null | undefined;
  urlToNavigate?: string;
  updateFilterOnlyIfDefaultUrl?: boolean;
  newTab?: boolean;
}

export interface UseWidgetFilterNavigatorReturn {
  onFilterClick: (
    filtersToAdd: FilterCondition[] | null,
    scopesToAdd?: FilterCondition[] | null
  ) => void;
}

const useWidgetFilterNavigator = ({
  filterToExecute,
  filterId,
  scopeToExecute,
  scopeId,
  urlToNavigate,
  updateFilterOnlyIfDefaultUrl = true,
  newTab = true,
}: UseWidgetFilterNavigatorOptions): UseWidgetFilterNavigatorReturn => {
  const { appViewNavigator } = useAppViewNavigator();
  const [filterToApply, setFilterToApply] =
    useState<InOutFilterData>(undefined);
  const [scopeToApply, setScopeToApply] = useState<InOutFilterData>(undefined);
  const [applyScopeInOut, setApplyScopeInOut] = useState(false);
  const [applyFilterInOut, setApplyFilterInOut] = useState(false);

  const filterData = useMemo(() => {
    let filter: FilterTreeNode | null;
    try {
      filter = !!filterToExecute ? JSON.parse(filterToExecute) : null;
    } catch (e) {
      filter = null;
    }

    // Preserve runtime: the falsy branch assigns a bare [{}], whose
    // `.filterConditions` is undefined.
    let filterObj = !!filter ? processFilter(filter) : [{}];
    // The processed groups feed useInOutButtons, which operates on them as the
    // FilterInOut domain shape (InOutFilterData); bridge from the processed type.
    return (filterObj as { filterConditions?: ProcessedConditionGroup[] })
      .filterConditions as unknown as InOutFilterData;
  }, [filterToExecute]);

  const { getScopeValueById } = useContext(ScopesContext);

  const scopeData = useMemo(() => {
    let scope: FilterTreeNode | null;
    try {
      scope = !!scopeToExecute ? JSON.parse(scopeToExecute) : null;
    } catch (e) {
      scope = null;
    }

    // Preserve runtime: the falsy branch assigns a bare [{}], whose
    // `.scopeConditions` is undefined.
    let filterObj = !!scope ? processScope(scope) : [{}];
    // See filterData: bridge the processed scope groups to the FilterInOut shape.
    return (filterObj as { scopeConditions?: ProcessedConditionGroup[] })
      .scopeConditions as unknown as InOutFilterData;
  }, [scopeToExecute]);

  const getShouldSetScopeToExecute = (
    // Runtime-preserved: callers pass either the stored scope string or the
    // in-memory processed groups (scopeToApply); for the latter the inequality
    // check below is always true, matching prior JS behavior.
    scopeToExecute: string | InOutFilterData | null | undefined
  ): boolean => {
    if (!scopeId || !scopeToExecute) {
      return false;
    }
    const currentScope = getScopeValueById(scopeId);
    const currentScopeQuery = currentScope?.value?.query;
    return currentScopeQuery !== scopeToExecute;
  };

  const updateCurrentFilter = (newFilter?: InOutFilterPart[] | null) => {
    setFilterToApply(newFilter);
  };

  const updateCurrentScope = (newScope?: InOutFilterPart[] | null) => {
    setScopeToApply(newScope);
  };

  useEffect(() => {
    // If we need to apply any navigation - we need to check whether the scope / filter is needed in the navigation
    // and then - if the callback to set the new data is finished, only then we can navigate with the wanted parameters
    const applyNavigator = applyFilterInOut || applyScopeInOut;
    const checkScope = applyScopeInOut ? !!scopeToApply : true;
    const checkFilter = applyFilterInOut ? !!filterToApply : true;
    if (applyNavigator && checkFilter && checkScope) {
      const shouldSetScopeToExecute = getShouldSetScopeToExecute(
        scopeToApply || scopeToExecute
      );
      appViewNavigator(
        filterId ?? null,
        scopeId ?? null,
        applyFilterInOut
          ? getFilterJson({
              filterPanels: filterToApply,
            } as unknown as FilterPanelsData)
          : (filterToExecute ?? null),
        applyScopeInOut
          ? getScopeJson({
              filterPanels: scopeToApply,
            } as unknown as FilterPanelsData)
          : shouldSetScopeToExecute
            ? (scopeToExecute ?? null)
            : null,
        newTab,
        urlToNavigate,
        updateFilterOnlyIfDefaultUrl
      );
      setApplyScopeInOut(false);
      setApplyFilterInOut(false);
    }
  }, [
    filterToApply,
    scopeToApply,
    applyScopeInOut,
    applyFilterInOut,
    updateFilterOnlyIfDefaultUrl,
    newTab,
  ]);

  const { onMultipleFilters } = useInOutButtons({
    filterData: filterData,
    applyFilter: updateCurrentFilter,
  });

  const { onMultipleFilters: onMultipleScopes } = useInOutButtons({
    filterData: scopeData,
    applyFilter: updateCurrentScope,
  });

  const onFilterClick = (
    filtersToAdd: FilterCondition[] | null,
    scopesToAdd?: FilterCondition[] | null
  ) => {
    // If the onClick affects scopes / filters - we set the flag to check the related data
    // before navigating, since the navigation will happen as an effect after both onMultipleFilters callbacks finish
    setApplyScopeInOut(!!scopesToAdd);
    setApplyFilterInOut(!!filtersToAdd);

    if (!!filtersToAdd) {
      onMultipleFilters(filtersToAdd, true);
    }
    if (!!scopesToAdd) {
      onMultipleScopes(scopesToAdd, true);
    }
  };

  return { onFilterClick };
};

export default useWidgetFilterNavigator;
